import React, { useState } from 'react';
import { X, Download, Eye, Calendar, User, AlertCircle, CheckCircle, Clock, XCircle, Search, FileText } from 'lucide-react';

const RequisitionStatusSection = ({ requisitions }) => {
  const [showRequisitionModal, setShowRequisitionModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);

  const handleRequisitionClick = (requisition) => {
    setSelectedRequisition(requisition);
    setShowRequisitionModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'in_progress':
        return <AlertCircle className="w-5 h-5 text-blue-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-50 text-emerald-800 border-emerald-200';
      case 'pending':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'rejected':
        return 'bg-red-50 text-red-800 border-red-200';
      case 'in_progress':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return '📄';
      case 'excel':
        return '📊';
      case 'word':
        return '📝';
      case 'zip':
        return '🗜️';
      default:
        return '📁';
    }
  };

  const filteredRequisitions = requisitions.filter(req =>
    req.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    req.submittedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
    req.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const requisitionsPerPage = 6;
  const totalPages = Math.ceil(filteredRequisitions.length / requisitionsPerPage);
  const [currentPage, setCurrentPage] = useState(0);

  const currentRequisitions = filteredRequisitions.slice(
    currentPage * requisitionsPerPage,
    (currentPage + 1) * requisitionsPerPage
  );

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0); // Reset to first page on search
  };

  const handleSearchClick = () => {
    setShowSearchInput(!showSearchInput);
    setSearchQuery(''); // Clear search query when toggling visibility
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-emerald-600" />
          <h2 className="text-2xl font-bold text-gray-800">Requisition Status</h2>
        </div>
        <div className="flex items-center gap-3">
          {showSearchInput && (
            <input
              type="text"
              placeholder="Search requisitions..."
              className="px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          )}
          <button
            onClick={handleSearchClick}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
            title="Search Requisitions"
          >
            <Search size={20} />
          </button>
        </div>
      </div>
      
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {currentRequisitions.length > 0 ? (
            currentRequisitions.map((requisition) => (
              <div
                key={requisition.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 border border-gray-100"
                onClick={() => handleRequisitionClick(requisition)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">
                      {requisition.title}
                    </h3>
                    {getStatusIcon(requisition.status)}
                  </div>
                  
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border mb-4 ${getStatusColor(requisition.status)}`}>
                    {requisition.status.replace('_', ' ').toUpperCase()}
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{requisition.submittedBy}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{requisition.submittedDate}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-500">
                      {requisition.files.length} files, {requisition.links.length} links
                    </span>
                    <Eye className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-10 text-gray-500">No requisitions found matching your search.</div>
          )}
        </div>

        {totalPages > 1 && (
          <>
            <button
              onClick={handlePrevPage}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors z-10 ml-2"
              aria-label="Previous slide"
            >
              &#9664;
            </button>
            <button
              onClick={handleNextPage}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors z-10 mr-2"
              aria-label="Next slide"
            >
              &#9654;
            </button>
          </>
        )}
      </div>

      {/* Requisition Modal */}
      {showRequisitionModal && selectedRequisition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
              <div className="flex items-center gap-3">
                {getStatusIcon(selectedRequisition.status)}
                <h3 className="text-xl font-bold">{selectedRequisition.title}</h3>
              </div>
              <button
                onClick={() => setShowRequisitionModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Requisition Details */}
                <div className="space-y-4">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(selectedRequisition.status)}`}>
                    {selectedRequisition.status.replace('_', ' ').toUpperCase()}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Submitted by</p>
                        <p className="font-medium">{selectedRequisition.submittedBy}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Submitted on</p>
                        <p className="font-medium">{selectedRequisition.submittedDate}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Files Section */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Uploaded Files ({selectedRequisition.files.length})
                  </h4>
                  
                  <div className="space-y-3">
                    {selectedRequisition.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getFileIcon(file.type)}</span>
                          <div>
                            <p className="font-medium text-gray-800">{file.name}</p>
                            <p className="text-sm text-gray-500">{file.size}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(file.url, '_blank')}
                          className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Links Section */}
              {selectedRequisition.links.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Related Links ({selectedRequisition.links.length})
                  </h4>
                  
                  <div className="space-y-3">
                    {selectedRequisition.links.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-blue-600">🔗</span>
                          <p className="font-medium text-blue-800">{link.title}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequisitionStatusSection;